import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import { type APIResponse, type ReportsMatterActivity } from "@/lib/types";

export type ReportsMatterActivityDateRangeType =
  | "ALL_TIME"
  | "PREVIOUS_7_DAYS"
  | "PREVIOUS_30_DAYS"
  | "PREVIOUS_90_DAYS"
  | "YEAR_TO_DATE"
  | "CUSTOM_DATE_RANGE";

export type RetrieveReportsMatterActivityInput = {
  dateRange: {
    endDate?: string;
    preset: ReportsMatterActivityDateRangeType;
    startDate?: string;
  };
  filters: {
    matterIds?: string[];
    matterStatusIds?: string[];
    incidentTypeIds?: string[];
    claimTypeIds?: string[];
    teamIds?: string[];
  };
  pageNumber?: number;
  pageSize?: number;
};

type MatterActivityReportV2Response = {
  metrics: {
    totalActions: number;
    totalActivityHours: number;
    totalEntries: number;
    uniqueMatters: number;
  };
  rows: Array<{
    actionCounts: Record<string, number>;
    activityDate: string;
    activityHours: number;
    matterId: string;
    matterStatusCategory: string;
    matterStatusLabel: string;
    matterTitle: string;
    incidentTypeLabel: string | null;
    claimTypes?: Array<{ id: string; label: string }>;
    teams: Array<{ label: string }>;
    timeline: Array<{ endAt: string; startAt: string }>;
  }>;
};

export const retrieveReportsMatterActivity = async (
  data: RetrieveReportsMatterActivityInput,
) => {
  const response = await authenticatedFastApiAxiosInstance.post<
    APIResponse<MatterActivityReportV2Response>
  >("reports/v2/matter-activity/query", {
    ...data,
    pageNumber: data.pageNumber ?? 1,
    pageSize: data.pageSize ?? 250,
  });

  return {
    data: response.data.data.rows.map(
      (row): ReportsMatterActivity => ({
        claimTypes: row.claimTypes ?? [],
        matterActivityReportAggregateActionsCompleted: Object.entries(
          row.actionCounts,
        ).map(([action, count]) => ({ [action]: count })),
        matterActivityReportAggregateActivityTotalHours: row.activityHours,
        matterActivityReportAggregateDate: row.activityDate,
        matterActivityReportAggregateMatterId: row.matterId,
        matterActivityReportAggregateMatterStatus: row.matterStatusLabel,
        matterActivityReportAggregateMatterStatusCategory:
          row.matterStatusCategory,
        matterActivityReportAggregateMatterTeam: row.teams.map(
          (team) => team.label,
        ),
        matterActivityReportAggregateMatterTitle: row.matterTitle,
        matterActivityReportAggregateIncidentType: row.incidentTypeLabel,
        matterActivityReportAggregateTimeline: row.timeline.map((range) => ({
          matterActivityReportDateTimeEnd: range.endAt,
          matterActivityReportDateTimeStart: range.startAt,
        })),
      }),
    ),
    meta: response.data.meta,
    metrics: response.data.data.metrics,
  };
};
